import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import './App.css';

// Authentication & New Pages
import Login from './pages/Login';
import ConversationHistory from './pages/ConversationHistory';
import RecordSessionNew from './pages/RecordSessionNew';

// Clinical Documentation Pages
import Dashboard from './pages/Dashboard';
import NurseDashboard from './pages/NurseDashboard';
import PatientDashboard from './pages/PatientDashboard';
import RecordSession from './pages/RecordSession';
import SessionView from './pages/SessionView';
import PatientManagement from './pages/PatientManagement';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Get role-appropriate dashboard component
  const getDashboardComponent = () => {
    if (!user || !user.role) return <Navigate to="/login" />;
    
    switch(user.role) {
      case 'doctor':
        return <Dashboard />;
      case 'nurse':
        return <NurseDashboard />;
      case 'patient':
        return <PatientDashboard />;
      default:
        return <Dashboard />;
    }
  };

  // Get navigation menu based on role
  const getNavigationMenu = () => {
    if (!user) return null;
    
    const role = user.role;
    
    if (role === 'patient') {
      return (
        <>
          <li className="nav-item">
            <Link to="/patient-dashboard" className="nav-link">My Portal</Link>
          </li>
          <li className="nav-item">
            <Link to="/history" className="nav-link">My Visits</Link>
          </li>
          <li className="nav-item">
            <span className="nav-text">{user.first_name} {user.last_name}</span>
          </li>
          <li className="nav-item">
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </li>
        </>
      );
    }
    
    if (role === 'nurse') {
      return (
        <>
          <li className="nav-item">
            <Link to="/nurse-dashboard" className="nav-link">Dashboard</Link>
          </li>
          <li className="nav-item">
            <Link to="/history" className="nav-link">Sessions</Link>
          </li>
          <li className="nav-item">
            <span className="nav-text">{user.first_name} {user.last_name}</span>
          </li>
          <li className="nav-item">
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </li>
        </>
      );
    }
    
    // Doctor menu (full access)
    return (
      <>
        <li className="nav-item">
          <Link to="/dashboard" className="nav-link">Dashboard</Link>
        </li>
        <li className="nav-item">
          <Link to="/record-new" className="nav-link">New Recording</Link>
        </li>
        <li className="nav-item">
          <Link to="/history" className="nav-link">History</Link>
        </li>
        <li className="nav-item">
          <Link to="/patients" className="nav-link">Patients</Link>
        </li>
        <li className="nav-item">
          <span className="nav-text">{user.first_name} {user.last_name}</span>
        </li>
        <li className="nav-item">
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </li>
      </>
    );
  };

  return (
    <Router>
      <div className="App">
        {user && (
          <nav className="navbar">
            <div className="nav-container">
              <Link to="/" className="nav-logo">
                🏥 Clinical Documentation AI
              </Link>
              <ul className="nav-menu">
                {getNavigationMenu()}
              </ul>
            </div>
          </nav>
        )}

        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
          
          {/* Protected Routes */}
          <Route path="/" element={user ? getDashboardComponent() : <Navigate to="/login" />} />
          <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
          <Route path="/nurse-dashboard" element={user ? <NurseDashboard /> : <Navigate to="/login" />} />
          <Route path="/patient-dashboard" element={user ? <PatientDashboard /> : <Navigate to="/login" />} />
          
          {/* New Routes */}
          <Route path="/history" element={user ? <ConversationHistory /> : <Navigate to="/login" />} />
          <Route path="/record-new" element={user ? <RecordSessionNew /> : <Navigate to="/login" />} />
          
          {/* Original Routes */}
          <Route path="/record" element={user ? <RecordSession /> : <Navigate to="/login" />} />
          <Route path="/session/:sessionId" element={user ? <SessionView /> : <Navigate to="/login" />} />
          <Route path="/patients" element={user && user.role === 'doctor' ? <PatientManagement /> : <Navigate to="/login" />} />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
        </Routes>

        {user && (
          <footer className="footer">
            <div className="container">
              <p>🔒 HIPAA Compliant • Encrypted • Secure</p>
              <p>&copy; 2025 Clinical Documentation AI. All data encrypted and protected.</p>
            </div>
          </footer>
        )}
      </div>
    </Router>
  );
}

export default App;